import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
} from "@mui/material";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import toast from "react-hot-toast";
import api from "../services/api";
import CameraCapture from "../components/CameraCapture";
import PunchSuccess from "../components/PunchSuccess";
import KioskHeader from "../components/KioskHeader";
import KioskStepBar from "../components/KioskStepBar";
import EmployeeSection from "../components/EmployeeSection";
import PunchActionButtons from "../components/PunchActionButtons";
import MobilePunchDock from "../components/MobilePunchDock";
import { savePhoto } from "../services/localStore";
import { submitPunch, isOnline, subscribeSyncStatus } from "../services/syncQueue";
import { BRAND, kioskCardSx } from "../styles/authTheme";

const RESET_DELAY_MS = 15000;
const MOBILE_DOCK_PADDING = 88;

const PunchPage = () => {
  const cameraRef = useRef(null);
  const resetTimerRef = useRef(null);
  const pollingRef = useRef(null);
  const pausePollingRef = useRef(false);

  const [employeeId, setEmployeeId] = useState("");
  const [employeeDbId, setEmployeeDbId] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [punchStatus, setPunchStatus] = useState(null);
  const [checkInTime, setCheckInTime] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [punching, setPunching] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);

  const [detectStatus, setDetectStatus] = useState("Please look at the camera.");

  const hasValidEmployee = Boolean(employeeName);
  const activeStep = hasValidEmployee ? 2 : 1;
  const showMobileDock = !successState && hasValidEmployee;

  const resetScreen = useCallback(() => {
    setEmployeeId("");
    setEmployeeDbId("");
    setEmployeeName("");
    setPunchStatus(null);
    setCheckInTime(null);
    setSuccessState(null);
    setDetectStatus("Please look at the camera.");
    startFacePolling();
  }, []);

  const handleRecognize = async (base64Image) => {
    try {
      setLoadingStatus(true);
      setDetectStatus("Scanning face...");
      const res = await api.post('ai/recognize-face/', {
        image: base64Image
      });
      
      if (res.data.success) {
        // Stop polling once recognized
        stopFacePolling();
        
        const emp = res.data.employee;
        setEmployeeDbId(emp.id);
        setEmployeeId(emp.employee_id || emp.id);
        const firstName = emp.first_name || "";
        const lastName = emp.last_name || "";
        setEmployeeName(`${firstName} ${lastName}`.trim());
        
        // For the scope of this implementation, we map is_punched_in to a status string 
        // to show correct buttons
        setPunchStatus(res.data.is_punched_in ? 'check_in' : 'check_out'); 
        
        // Schedule auto-reset if user doesn't punch
        scheduleReset();
      }
    } catch (err) {
       const status = err.response?.data?.status;
       if (status === "NO_MATCH") {
           setDetectStatus("Your face is not registered.");
           pausePollingRef.current = true;
           setTimeout(() => {
               pausePollingRef.current = false;
               setDetectStatus("Please look at the camera.");
           }, 4000);
       } else if (status === "YOLO_SPOOF" || status === "CHALLENGE_REQUIRED") {
           setDetectStatus("Please look straight at the camera and hold still.");
           pausePollingRef.current = true;
           setTimeout(() => {
               pausePollingRef.current = false;
               setDetectStatus("Please look at the camera.");
           }, 3000);
       } else if (status === "NO_FACE" || status === "MULTIPLE_FACES") {
           setDetectStatus("Please ensure one clear face is visible.");
       } else if (status === "BLURRY") {
           setDetectStatus("Image is blurry. Hold still.");
       } else {
           setDetectStatus("Scanning...");
       }
    } finally {
      setLoadingStatus(false);
    }
  };

  const startFacePolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    pollingRef.current = setInterval(async () => {
      if (cameraRef.current && !loadingStatus && !hasValidEmployee && !successState && !pausePollingRef.current) {
        const photoBlob = await cameraRef.current.capturePhoto();
        if (photoBlob) {
           const reader = new FileReader();
           reader.readAsDataURL(photoBlob);
           reader.onloadend = () => {
              const base64data = reader.result;
              handleRecognize(base64data);
           }
        }
      }
    }, 2000); // Check every 2 seconds
  };

  const stopFacePolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => {
    startFacePolling();
    return () => stopFacePolling();
  }, [hasValidEmployee, loadingStatus, successState]);

  useEffect(() => {
    const handleOnlineChange = () => setOnline(isOnline());
    window.addEventListener("online", handleOnlineChange);
    window.addEventListener("offline", handleOnlineChange);
    return () => {
      window.removeEventListener("online", handleOnlineChange);
      window.removeEventListener("offline", handleOnlineChange);
    };
  }, []);

  useEffect(() => {
    return subscribeSyncStatus(({ pending = 0 }) => {
      setPendingCount(pending);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const scheduleReset = () => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = setTimeout(resetScreen, RESET_DELAY_MS);
  };

  const handlePunch = async (action) => {
    if (!employeeId) {
      toast.error("Employee not recognized");
      return;
    }

    setPunching(true);
    const timestamp = new Date().toISOString();
    let queued = false;

    try {
      const photoBlob = await cameraRef.current?.capturePhoto?.();
      if (photoBlob) {
        await savePhoto({
          employeeId: employeeId,
          type: action,
          timestamp,
          blob: photoBlob,
        });
      }

      const result = await submitPunch(employeeId, action, timestamp);
      setSuccessState({
        employeeName: result.employee_name || employeeName,
        action,
        timestamp: result.timestamp || timestamp,
        queued: false,
      });
      toast.success(action === "check_in" ? "Punched in!" : "Punched out!");
      scheduleReset();
    } catch (error) {
      if (error.code === "OFFLINE_QUEUED") {
        queued = true;
        setSuccessState({
          employeeName: employeeName || employeeId,
          action,
          timestamp,
          queued: true,
        });
        toast.success("Saved offline — will sync when online");
        scheduleReset();
      } else {
        const message =
          error.response?.data?.error ||
          error.response?.data?.detail ||
          error.message ||
          "Punch failed";
        toast.error(message);
      }
    } finally {
      setPunching(false);
      if (queued) {
        setPendingCount((c) => c + 1);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BRAND.bg,
        backgroundImage:
          "radial-gradient(circle at 20% 0%, rgba(230,90,0,0.05) 0%, transparent 42%), radial-gradient(circle at 80% 100%, rgba(26,43,74,0.04) 0%, transparent 38%)",
        pb: { xs: showMobileDock ? `${MOBILE_DOCK_PADDING}px` : 0, md: 0 },
      }}
    >
      <KioskHeader
        title="Attendance Kiosk"
        subtitle="Worker check-in / check-out"
        online={online}
        pendingCount={pendingCount}
      />

      <Box
        sx={{
          px: { xs: 1.25, sm: 3, md: 4 },
          py: { xs: 1.25, sm: 3, md: 4 },
          maxWidth: 720,
          mx: "auto",
        }}
      >
        <Box sx={{ ...kioskCardSx, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: `linear-gradient(90deg, ${BRAND.orange} 0%, ${BRAND.navy} 100%)`,
            }}
          />

          {successState ? (
            <Box sx={{ pt: 1 }}>
              <PunchSuccess {...successState} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  textAlign: { xs: "left", sm: "center" },
                  mb: { xs: 1, sm: 1.5 },
                  pt: { xs: 0.25, sm: 0.5 },
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", sm: "inline-flex" },
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    mb: 1.5,
                    borderRadius: "999px",
                    bgcolor: BRAND.orangeLight,
                  }}
                >
                  <FingerprintOutlinedIcon sx={{ fontSize: 18, color: BRAND.orange }} />
                  <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 700 }}>
                    Face AI Attendance
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    color: BRAND.navy,
                    fontWeight: 800,
                    mb: 0.25,
                    letterSpacing: "-0.02em",
                    fontSize: { xs: "1.1rem", sm: "1.5rem" },
                    display: { xs: hasValidEmployee ? "none" : "block", sm: "block" },
                  }}
                >
                  Mark your attendance
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: BRAND.muted,
                    maxWidth: 360,
                    mx: { xs: 0, sm: "auto" },
                    fontSize: { xs: "0.78rem", sm: "0.875rem" },
                    display: { xs: hasValidEmployee ? "none" : "block", sm: "block" },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                    Look at camera, then punch.
                  </Box>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Look at the camera for auto-detection, then punch in or out.
                  </Box>
                </Typography>
              </Box>

              {/* Simplified StepBar since we removed ID input */}
              <KioskStepBar activeStep={activeStep} compact={hasValidEmployee} />

              <Grid container spacing={{ xs: 1.25, sm: 2, md: 3 }}>
                <Grid item xs={12} md={5}>
                  {hasValidEmployee ? (
                     <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ color: BRAND.navy, mb: 1 }}>
                            Welcome, {employeeName}!
                        </Typography>
                        <Typography variant="body2" sx={{ color: BRAND.muted }}>
                            Employee ID: {employeeId}
                        </Typography>
                        <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                            {punchStatus === 'check_in' ? 'You are currently Punched In.' : 'You are currently Punched Out.'}
                        </Typography>
                     </Box>
                  ) : (
                     <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                         {detectStatus === "Please look at the camera." ? (
                             <Typography variant="body1" sx={{ color: BRAND.muted, textAlign: 'center' }}>
                                 {detectStatus}
                             </Typography>
                         ) : detectStatus === "Scanning face..." ? (
                             <Typography variant="body1" sx={{ color: BRAND.orange, fontWeight: 'bold' }}>
                                 {detectStatus}
                             </Typography>
                         ) : (
                             <Box sx={{ bgcolor: '#ffebee', border: '1px solid #f44336', borderRadius: 2, p: 2, width: '100%' }}>
                               <Typography variant="h6" sx={{ color: '#d32f2f', textAlign: 'center', fontWeight: 'bold' }}>
                                   {detectStatus}
                               </Typography>
                             </Box>
                         )}
                     </Box>
                  )}

                  <PunchActionButtons
                    sx={{ display: { xs: "none", md: "flex" } }}
                    punchStatus={punchStatus}
                    hasValidEmployee={hasValidEmployee}
                    loadingStatus={loadingStatus}
                    punching={punching}
                    onPunchIn={() => handlePunch("check_in")}
                    onPunchOut={() => handlePunch("check_out")}
                  />
                </Grid>

                <Grid item xs={12} md={7}>
                  <CameraCapture ref={cameraRef} />
                </Grid>
              </Grid>
            </>
          )}
        </Box>

        <Typography
          variant="caption"
          display="block"
          align="center"
          sx={{
            mt: { xs: 1.5, sm: 3 },
            color: BRAND.muted,
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            display: { xs: "none", sm: "block" },
          }}
        >
          Copyright © 2026 — Swatsan Tech Pvt. Ltd.
        </Typography>
      </Box>

      <MobilePunchDock
        visible={showMobileDock}
        punchStatus={punchStatus}
        hasValidEmployee={hasValidEmployee}
        loadingStatus={loadingStatus}
        punching={punching}
        onPunchIn={() => handlePunch("check_in")}
        onPunchOut={() => handlePunch("check_out")}
      />
    </Box>
  );
};

export default PunchPage;
